/**
 * Feedback API Service
 * Handles all feedback-related API calls
 */

import supabase from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get authentication headers
 */
async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  return {
    'Authorization': token ? `Bearer ${token}` : '',
  };
}

/**
 * Handle API response
 */
async function handleResponse(response) {
  if (!response.ok) {
    let errorBody = {};
    try {
      errorBody = await response.json();
    } catch (e) {
      errorBody = { message: response.statusText || 'Unknown error' };
    }
    throw new Error(errorBody.message || errorBody.error || 'Request failed');
  }
  return response.json();
}

// ============================================
// User API Functions
// ============================================

/**
 * Submit new feedback with optional images
 * @param {string} message - Feedback message
 * @param {File[]} images - Array of image files (max 5)
 * @returns {Promise<Object>} Created feedback
 */
export async function submitFeedback(message, images = []) {
  const authHeaders = await getAuthHeaders();
  const formData = new FormData();

  formData.append('message', message);

  // Add images (max 5)
  images.slice(0, 5).forEach((image) => {
    formData.append('images', image);
  });

  const response = await fetch(`${API_URL}/api/feedback`, {
    method: 'POST',
    headers: authHeaders,
    body: formData
  });

  return handleResponse(response);
}

/**
 * Get user's feedback history
 * @param {Object} options - { limit, offset }
 * @returns {Promise<Object>} { data: { feedbacks, total } }
 */
export async function getUserFeedback(options = {}) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();

  if (options.limit) params.append('limit', options.limit);
  if (options.offset) params.append('offset', options.offset);

  const response = await fetch(`${API_URL}/api/feedback?${params.toString()}`, {
    headers: authHeaders
  });

  return handleResponse(response);
}

/**
 * Get single feedback detail
 * @param {string} feedbackId - Feedback ID
 * @returns {Promise<Object>} Feedback with images
 */
export async function getFeedbackById(feedbackId) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/feedback/${feedbackId}`, {
    headers: authHeaders
  });

  return handleResponse(response);
}

// ============================================
// Admin API Functions
// ============================================

/**
 * Get all feedback (admin only)
 * @param {Object} filters - { status, limit, offset, search }
 * @returns {Promise<Object>} { data: { feedbacks, total } }
 */
export async function getAllFeedbackAdmin(filters = {}) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  if (filters.search) params.append('search', filters.search);

  const response = await fetch(`${API_URL}/api/feedback/admin/all?${params.toString()}`, {
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json'
    }
  });

  return handleResponse(response);
}

/**
 * Reply to feedback (admin only)
 * @param {string} feedbackId - Feedback ID
 * @param {string} reply - Reply message
 * @returns {Promise<Object>} Updated feedback
 */
export async function replyToFeedback(feedbackId, reply) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/feedback/admin/${feedbackId}/reply`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ reply })
  });

  return handleResponse(response);
}

/**
 * Mark feedback as resolved (admin only)
 * @param {string} feedbackId - Feedback ID
 * @returns {Promise<Object>} Updated feedback
 */
export async function resolveFeedback(feedbackId) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/feedback/admin/${feedbackId}/resolve`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json'
    }
  });

  return handleResponse(response);
}

/**
 * Get feedback statistics (admin only)
 * @returns {Promise<Object>} Statistics
 */
export async function getFeedbackStatistics() {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/feedback/admin/statistics`, {
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json'
    }
  });

  return handleResponse(response);
}
